import React, { useMemo, useState } from 'react';
import {
  Popper, Paper, Box, ToggleButtonGroup, ToggleButton, TextField, InputAdornment,
  Chip, List, ListItemButton, ListItemAvatar, Avatar, ListItemText, Divider, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const GLASS = { text: '#e5e7eb', subtext: '#9ca3af' };

const chipToneSx = (tone = 'neutral') => ({
  bgcolor: tone==='success' ? 'rgba(16,185,129,.18)' : tone==='error' ? 'rgba(239,68,68,.18)' : 'rgba(148,163,184,.14)',
  color:   tone==='success' ? '#34d399'            : tone==='error' ? '#f87171'             : '#cbd5e1',
  border:  tone==='success' ? '1px solid rgba(16,185,129,.45)' :
          tone==='error'   ? '1px solid rgba(239,68,68,.45)' :
                             '1px solid rgba(148,163,184,.28)',
  '& .MuiChip-label': { px: 0.75, fontWeight: 800, fontSize: 11, lineHeight: '20px' },
});

const ChipTone = ({ label, $pos='flat', size='small' }) => (
  <Chip
    size={size}
    label={label}
    sx={{
      height: 20,
      borderRadius: 1,
      border:
        $pos === 'up'
          ? '1px solid rgba(16,185,129,.45)'
          : $pos === 'down'
          ? '1px solid rgba(239,68,68,.45)'
          : '1px solid rgba(148,163,184,.30)',
      color:
        $pos === 'up'
          ? '#34d399'
          : $pos === 'down'
          ? '#f87171'
          : '#cbd5e1',
      background:
        $pos === 'up'
          ? 'rgba(16,185,129,.18)'
          : $pos === 'down'
          ? 'rgba(239,68,68,.18)'
          : 'rgba(148,163,184,.14)',
      '& .MuiChip-label': { paddingInline: 8, fontSize: 12, lineHeight: '20px', fontWeight: 800 },
    }}
    variant="outlined"
  />
);

const hypeFromChanges = (c24 = 0, c7 = 0) => {
  if (c24 > 5 || c7 > 10) return 'Hype';
  if (c24 < -5 || c7 < -10) return 'Oblivion';
  return 'Neutral';
};
const hypeTone = (s = '') =>
  s.startsWith('Hype') ? 'success' :
  s.startsWith('Oblivion') ? 'error' :
  'neutral';

const fmtPriceTight = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(2);
  if (n >= 0.01) return n.toFixed(4);
  return n.toFixed(6);
};

export default function PairPicker({ anchorEl, open, onClose, list = [], onSelect }) {
  const [q, setQ] = useState('');
  const [mode, setMode] = useState('all');
  const [sortKey, setSortKey] = useState('change');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) { setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')); return prev; }
      setSortDir('desc');
      return key;
    });
  };

  const shortHype = (s = '') => (s?.startsWith('Hype') ? 'Hype' : s?.startsWith('Oblivion') ? 'Oblv' : 'Neutral');
  const hypeScore = (s = '') => (s?.startsWith('Hype') ? 2 : s?.startsWith('Neutral') ? 1 : 0);

  const filtered = useMemo(() => {
    const base = list.map((t) => ({
      ...t,
      symbol: (t.symbol || '').toUpperCase(),
      price: t.usd ?? t.price,
      change24h: t.usd_24h_change ?? t.change24h ?? 0,
      change7d: t.usd_7d_change ?? t.change7d ?? 0,
      hype: hypeFromChanges(t.usd_24h_change ?? 0, t.usd_7d_change ?? 0),
    }));
    let arr = base;

    const s = q.trim().toLowerCase();
    if (s) {
      arr = arr.filter(t =>
        t.symbol.toLowerCase().includes(s) ||
        String(t.name || '').toLowerCase().includes(s)
      );
    }

    if (mode === 'gainers') arr = arr.filter((t) => Number(t.change24h) > 0);
    if (mode === 'losers')  arr = arr.filter((t) => Number(t.change24h) < 0);
    if (mode === 'hype')    arr = arr.filter((t) => t.hype === 'Hype');

    if (sortKey === 'change') {
      arr = arr.sort((a, b) => {
        const av = Number(a.change24h), bv = Number(b.change24h);
        return sortDir === 'desc' ? (bv - av) : (av - bv);
      });
    } else {
      arr = arr.sort((a, b) => {
        const ah = hypeScore(a.hype), bh = hypeScore(b.hype);
        return sortDir === 'desc' ? (bh - ah) : (ah - bh);
      });
    }

    return arr.slice(0, 150);
  }, [list, q, mode, sortKey, sortDir]);

  return (
    <Popper open={open} anchorEl={anchorEl} placement="bottom" sx={{ zIndex: 2000 }}>
      <Paper
        elevation={8}
        sx={{
          width: 620,
          maxWidth: '94vw',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'rgba(12,16,22,.96)',
          color: GLASS.text,
          border: '1px solid rgba(255,255,255,.10)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,.08)' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: '8px !important',
                  height: 28, fontSize: 11,
                  color: GLASS.text,
                  borderColor: 'rgba(255,255,255,.16)',
                  '&.Mui-selected': { background: 'rgba(147,197,253,.18)', borderColor: 'rgba(147,197,253,.45)', color: '#fff' },
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="gainers">Gainers</ToggleButton>
              <ToggleButton value="losers">Losers</ToggleButton>
              <ToggleButton value="hype">Hype</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              placeholder="Search pair…"
              value={q}
              onChange={(e) => setQ(e.target.value.replace(/<[^>]*>?/gm, '').slice(0, 40))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: GLASS.subtext }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                '& .MuiInputBase-input': { color: GLASS.text, fontSize: 13, fontWeight: 700 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.16)' },
              }}
            />
          </Box>

          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Chip
              size="small"
              clickable
              label={`24h Chg ${sortKey==='change' ? (sortDir==='desc'?'▼':'▲') : ''}`}
              onClick={() => toggleSort('change')}
              sx={{
                height: 24,
                borderRadius: 1,
                bgcolor: sortKey==='change' ? 'rgba(147,197,253,.18)' : 'rgba(255,255,255,.06)',
                border:  sortKey==='change' ? '1px solid rgba(147,197,253,.45)' : '1px solid rgba(255,255,255,.22)',
                color: '#e5e7eb',
                '& .MuiChip-label': { fontWeight: 900, fontSize: 11, lineHeight: '24px', px: 1 },
                '&:hover': { bgcolor: 'rgba(147,197,253,.24)' },
              }}
              variant="outlined"
            />
            <Chip
              size="small"
              clickable
              label={`Hype ${sortKey==='hype' ? (sortDir==='desc'?'▼':'▲') : ''}`}
              onClick={() => toggleSort('hype')}
              sx={{
                height: 24,
                borderRadius: 1,
                bgcolor: sortKey==='hype' ? 'rgba(147,197,253,.18)' : 'rgba(255,255,255,.06)',
                border:  sortKey==='hype' ? '1px solid rgba(147,197,253,.45)' : '1px solid rgba(255,255,255,.22)',
                color: '#e5e7eb',
                '& .MuiChip-label': { fontWeight: 900, fontSize: 11, lineHeight: '24px', px: 1 },
                '&:hover': { bgcolor: 'rgba(147,197,253,.24)' },
              }}
              variant="outlined"
            />
          </Box>
        </Box>

        <List dense disablePadding sx={{ maxHeight: 440, overflowY: 'auto' }}>
          {filtered.map((t) => {
            const ch = Number(t.change24h ?? 0);
            const hypeLabel = shortHype(hypeFromChanges(t.usd_24h_change ?? 0, t.usd_7d_change ?? 0));
            const hypeToneKey = hypeTone(hypeLabel);

            return (
              <React.Fragment key={`${t.id || t.symbol}`}>
                <ListItemButton
                  onClick={() => { onSelect?.(t); onClose?.(); }}
                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,.06)' }, py: 0.5 }}
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar
                      src={t.logo}
                      alt={t.symbol}
                      sx={{ width: 22, height: 22, bgcolor: '#0b1320' }}
                      imgProps={{ referrerPolicy: 'no-referrer' }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                        <Typography component="span" sx={{ fontWeight: 900, color: '#fff' }}>
                          {(t.symbol || '').toUpperCase()}/USDT
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ color: GLASS.subtext }}>
                          {t.name}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                        <Typography component="span" variant="caption" sx={{ color: '#e5e7eb' }}>
                          ${fmtPriceTight(t.usd ?? t.price)}
                        </Typography>
                        <ChipTone
                          label={`${ch >= 0 ? '+' : ''}${(ch).toFixed(2)}%`}
                          $pos={ch > 0 ? 'up' : ch < 0 ? 'down' : 'flat'}
                          size="small"
                        />
                        <Chip
                          size="small"
                          label={hypeLabel}
                          variant="outlined"
                          sx={{ height: 20, borderRadius: 1, ...chipToneSx(hypeToneKey) }}
                        />
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider sx={{ opacity: .06 }} />
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    </Popper>
  );
}
